import {
  IMPORT_FAILURE_REASON,
  IMPORT_TRIGGER,
} from '@/constant/eventActions/ImportEventActions'

// Payload builders for the import outcome events, plus the de-duplication that
// keeps importing-successful to one per import.
//
// This file emits nothing and imports nothing but constants — deliberately.
// The helpers used to live in ai-chatbot-import-pptx-pdf.module.js, which
// imports the store, so a component that merely wanted to report an outcome
// dragged the whole vuex graph into its test environment. Moving them out only
// works if they stay store-free: @/util and @/utils/mixpanel.utils BOTH import
// @/vuex/store, which imports the vuex modules, which import this file — a
// cycle, and one strict lint rejects outright. So callers pass their own
// trackGA4AndMixpanel and their own eventCategory (which needs @/router, same
// problem). Every caller already has both.

const buildImportTrackingData = (fileInfo) => ({
  eventCategory: fileInfo?.eventCategory,
  file_name: fileInfo?.file_name,
  file_type: fileInfo?.file_type,
  file_metadata: fileInfo?.file_metadata,
  import_option: fileInfo?.import_option,
  provider: fileInfo?.provider,
  source: fileInfo?.source,
  // Which surface started this import. Defaults to the modal because that was
  // the only emitter of these events until the cloud re-sync joined them, so
  // every historical event is a modal import and the series stays comparable.
  // Filtering on it recovers the pre-re-sync report exactly.
  import_trigger: fileInfo?.import_trigger || IMPORT_TRIGGER.MODAL,
})

const buildImportFailedData = (
  fileInfo,
  error,
  reason = IMPORT_FAILURE_REASON.IMPORT_JOB,
) => ({
  ...buildImportTrackingData(fileInfo),
  // WHERE the import died, so importing-failed can be broken down by cause
  // instead of being one flat number. Defaults to IMPORT_JOB because that was
  // the only failure this event ever reported — every pre-existing call site
  // means "the job failed", so historical data keeps its meaning.
  reason,
  error_message: error?.body?.message || error?.message || 'Import failed',
})

// One importing-successful per import, however many places notice it finished.
//
// Two call sites report success and neither can be removed: the import action
// fires even with no editor mounted, and the socket-driven clean-up in
// data-third-party-action is the ONLY success signal the agent path has. With
// an editor open both run, milliseconds apart — which has been double-counting
// importing-successful since 5e3d3c36b4 (Nov 2025).
//
// De-duplicating here beats restructuring the flag lifecycle, which the toaster
// depends on and which already carries three ownership guards.
const successReportedFor = new Set()

// What the import looked like at hand-off, for the paths that report their
// outcome long after the state describing it has been cleared.
//
// "Import and generate slides with AI" nulls aiChatbotUploadedFile immediately
// after the postMessage, but its success is reported minutes later by the
// socket-driven clean-up, which reads that null — so every agent-path
// importing-successful has shipped with no file_name, file_type, provider or
// source, only import_option. That is precisely the breakdown needed to tell
// which kind of deck fails.
const importInfoFor = new Map()

const rememberImportInfo = (presentationId, info) => {
  if (presentationId != null) importInfoFor.set(presentationId, info)
}

const recallImportInfo = (presentationId) => (
  presentationId == null ? undefined : importInfoFor.get(presentationId)
)

// Called when an import STARTS on a deck, so a genuine re-import reports again.
// Also drops the previous import's snapshot, so a later report can never pick
// up the deck's last-but-one file.
const forgetImportSuccessFor = (presentationId) => {
  if (presentationId == null) return
  successReportedFor.delete(presentationId)
  importInfoFor.delete(presentationId)
}

// `presentationId` is optional: a caller that cannot identify its import is
// never de-duplicated, which is the safe direction — an extra event beats a
// missing one. A re-sync passes none deliberately; it goes through neither of
// the two racing call sites, and keying it by deck would suppress a second
// legitimate re-sync of the same presentation.
const shouldReportImportSuccess = (presentationId) => {
  if (presentationId == null) return true
  if (successReportedFor.has(presentationId)) return false
  successReportedFor.add(presentationId)
  return true
}

export {
  buildImportTrackingData,
  buildImportFailedData,
  forgetImportSuccessFor,
  rememberImportInfo,
  recallImportInfo,
  shouldReportImportSuccess,
}
